import React from 'react';
import SolutionPageTemplate from './SolutionPageTemplate';

const GRAPH_CODE = `// graph.mjs — your CI/CD pipeline
import { WorkflowGraph, z } from '@zibby/agent-workflow';

const PlanSchema   = z.object({ files: z.array(z.string()), risks: z.array(z.string()) });
const DiffSchema   = z.object({ patch: z.string(), filesChanged: z.array(z.string()) });
const ResultSchema = z.object({ passed: z.boolean(), report: z.string() });

const graph = new WorkflowGraph();

graph
  .addNode('plan',      { agent: 'claude', prompt, outputSchema: PlanSchema   })
  .addNode('implement', { agent: 'cursor', prompt, outputSchema: DiffSchema   })
  .addNode('verify',    { agent: 'codex',  prompt, outputSchema: ResultSchema });

graph.addEdge('plan', 'implement');
graph.addEdge('implement', 'verify');
graph.setEntryPoint('plan');

export default graph;`;

const CLI_CODE = `# Local dev with hot reload
zibby workflow start my-pipeline

# Ship to cloud (Heroku-style bundle, ~3s cold start)
zibby workflow deploy my-pipeline
# → returns a UUID

# Trigger from CI/CD
zibby workflow trigger <uuid> -t

# Or from any HTTP client
curl -X POST https://api.zibby.app/v1/workflows/<uuid>/trigger \\
  -H "Authorization: Bearer $ZIBBY_API_KEY"`;

const WorkflowEnginePage = () => {
  return (
    <SolutionPageTemplate
      meta={{
        title: 'Zibby Workflow Engine — multi-agent pipelines for production',
        description: 'Compose Claude Code, Cursor, Codex, and Gemini into structured workflows with Zod-validated handoff. Vendor-neutral. JavaScript-first. Local + cloud.',
      }}
      eyebrow="PLATFORM"
      title="The cloud pipeline for coding agents."
      lede="Compose Claude Code, Cursor, Codex, and Gemini into structured workflows with Zod-validated handoff between nodes. Vendor-neutral. JavaScript-first. Runs locally with hot reload, deploys to our cloud as Heroku-style bundles."
      ctaPrimary={{ label: 'Get started free', href: '/connect' }}
      ctaSecondary={{ label: 'Read the docs →', href: 'https://docs.zibby.app', external: true }}
      sections={[
        {
          title: 'A graph of real coding agents.',
          body: (
            <>
              <p>
                Each node hands off to a real coding-agent CLI — Claude Code, cursor-agent, codex CLI — that owns its own tool loop, file edits, and multi-turn reasoning. <strong>You don't reinvent agents. You orchestrate the ones that already exist.</strong>
              </p>
              <p>
                Mix vendors per node: Claude for planning, Cursor for implementation, Codex for verification. Every node's output is validated against a Zod schema before the next runs — no prompt-stuffing, no free-form-text-passing-through-pipelines surprise.
              </p>
            </>
          ),
          code: GRAPH_CODE,
        },
        {
          title: 'Run anywhere.',
          body: (
            <>
              <p>
                Local dev with hot reload — see your graph re-execute on every save. When you're happy, ship to cloud as a pre-built Heroku-style bundle (~3s cold start, no runtime npm install).
              </p>
              <p>
                Trigger from CI/CD, webhooks, or any HTTP client. Tail logs live with <code style={{ background: 'rgba(255,255,255,0.06)', padding: '2px 6px', borderRadius: 4, fontFamily: 'SF Mono, Menlo, monospace', fontSize: '13px' }}>zibby workflow logs &lt;uuid&gt; -t</code>.
              </p>
            </>
          ),
          code: CLI_CODE,
        },
        {
          title: 'Vendor-neutral by design — structurally.',
          body: (
            <>
              <p>
                Every coding-agent vendor will keep adding orchestration to their own CLI. But <strong>Anthropic will never help you orchestrate Codex. OpenAI will never help you orchestrate Claude.</strong> Multi-vendor neutrality is structural, not a feature anyone else can add.
              </p>
              <table className="sp-table">
                <thead>
                  <tr><th></th><th>Zibby</th><th>Claude Code Agent Teams</th><th>Devin</th><th>Mastra / LangGraph / CrewAI</th></tr>
                </thead>
                <tbody>
                  <tr><td>Wraps real coding-agent CLIs</td><td className="sp-yes">✓ Claude + Codex + Cursor + Gemini</td><td className="sp-no">Claude only</td><td className="sp-no">proprietary</td><td className="sp-no">wraps LLM APIs</td></tr>
                  <tr><td>Multi-vendor</td><td className="sp-yes">✓</td><td className="sp-no">—</td><td className="sp-no">—</td><td className="sp-no">N/A</td></tr>
                  <tr><td>Cloud-hosted</td><td className="sp-yes">✓</td><td className="sp-no">local IDE</td><td className="sp-yes">✓</td><td className="sp-no">varies</td></tr>
                  <tr><td>Schema-enforced handoff</td><td className="sp-yes">✓ Zod</td><td className="sp-no">free-form</td><td className="sp-no">proprietary</td><td className="sp-yes">✓</td></tr>
                  <tr><td>Static egress IP addon</td><td className="sp-yes">✓</td><td className="sp-no">—</td><td className="sp-no">—</td><td className="sp-no">—</td></tr>
                </tbody>
              </table>
            </>
          ),
        },
        {
          title: 'Production primitives, not local IDE demos.',
          body: (
            <>
              <p>The cloud isn't an afterthought — every Zibby workflow ships with:</p>
              <ul>
                <li><strong>SSE log streaming</strong> with auto-reconnect — tail any execution from anywhere</li>
                <li><strong>Per-execution session folders</strong> in a stable on-disk JSONL format — replay any node from any past run via <code>--session</code></li>
                <li><strong>Runtime caps + tier-based limits</strong> (15 min free, 30 min Pro, configurable per account)</li>
                <li><strong>Dedicated egress IP</strong> addon — pin all outbound HTTPS through one whitelistable IP for self-hosted GitLab / Salesforce / Oracle Cloud</li>
                <li><strong>Heroku-style bundles</strong> — pre-built tarballs eliminate runtime npm install</li>
              </ul>
            </>
          ),
        },
      ]}
      cta={{
        title: 'Compose your first multi-agent pipeline in 60 seconds.',
        body: 'No setup. No credit card. Run locally, then deploy to cloud when you\'re ready.',
        primary: { label: 'Get started free', href: '/connect' },
        secondary: { label: 'Read the docs →', href: 'https://docs.zibby.app', external: true },
      }}
    />
  );
};

export default WorkflowEnginePage;
